// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import type { MutableRefObject } from 'react'
import { SaveStatusBadge } from '@/components/ui/SaveStatusBadge'
import { PrimaryActionButton } from '@/components/ui/PrimaryActionButton'
import { SecondaryActionButton } from '@/components/ui/SecondaryActionButton'
import { InfoButton }      from '@/components/ui/InfoButton'
import { ModuleShell }     from '@/components/ui/ModuleShell'
import { RegieCoachBlock } from '@/components/process/RegieCoachBlock'
import { ClosingGuidance } from '@/components/process/ClosingGuidance'
import { Textarea, Input, Select, Checkbox } from '@/components/ui'
import { Card }            from '@/components/ui/Card'
import { Badge }           from '@/components/ui/Badge'
import { LoadingState }    from '@/components/feedback/LoadingState'
import { useAuthStore }    from '@/store'
import { ROUTES }     from '@/lib/constants/routes'
import { useReturnNav } from '@/lib/returnNav'
import { useAutosave }     from '@/hooks/useAutosave'
import { loadPersonalFarewell, savePersonalFarewell, loadMessages, saveMessage, type PersonalFarewellData } from '@/services/wishes'
import { loadPartner, loadChildren, type PartnerData, type ChildEntry } from '@/services/wizard'
import { Share2, CheckCircle2, ChevronRight, Printer, Send } from 'lucide-react'

const ROUWKAART_TEMPLATES = [
  { value: 'modern',      label: 'Modern - strak en tijdloos' },
  { value: 'klassiek',    label: 'Klassiek - traditioneel' },
  { value: 'religieus',   label: 'Religieus - met tekst of symbool' },
  { value: 'minimalistisch', label: 'Minimalistisch - eenvoudig' },
]

const DEMO: PersonalFarewellData & Record<string, string> = {
  generalMessage: 'Lieve mensen die mij dierbaar zijn,\n\nAls jullie dit lezen ben ik er niet meer. Ik hoop dat mijn leven ook jullie leven heeft verrijkt, zoals jullie mijn leven hebben verrijkt.\n\nIk heb geprobeerd alles goed voor te bereiden, zodat jullie zo min mogelijk extra zorgen hebben.\n\n[Vul hier uw persoonlijke boodschap in]',
  letterPartner:  'Lieve [naam partner],\n\nJij bent de liefde van mijn leven. Onze jaren samen waren het mooiste wat ik me had kunnen wensen.\n\nDank je voor alles. Zorg goed voor jezelf.\n\n[Vul hier uw persoonlijke brief in]',
  letterChildren: 'Lieve [naam kind / kinderen],\n\nJullie zijn mijn grootste trots. Zorg goed voor jezelf en voor elkaar.\n\nIk houd van jullie, altijd.\n\n[Vul hier uw persoonlijke brief in]',
  letterFamily:   'Lieve familie,\n\nJullie zijn en blijven voor altijd mijn familie. Ik dank jullie voor de mooie herinneringen.\n\n[Vul hier uw persoonlijke brief in]',
  letterFriends:  'Lieve vrienden,\n\nVriendschap is een van de mooiste dingen in het leven. Jullie zijn daarin bijzonder geweest voor mij.\n\n[Vul hier uw persoonlijke brief in]',
  practicalNotes: 'Praktische aanwijzingen voor mijn naasten:\n\n• De reservesleutel hangt bij de buren rechts naast ons\n• Mijn wachtwoorden staan in een envelop bij de notaris\n• Abonnementen: zie de map op mijn bureau\n• Mijn arts: huisarts [naam], tel. [nummer]\n\n[Vul hier uw praktische aanwijzingen in]',
}

interface FarewellExtended extends PersonalFarewellData {
  rouwkaartTemplate?: string
  rouwkaartName?: string
  rouwkaartBirthDate?: string
  rouwkaartText?: string
  rouwkaartPoem?: string
  rouwkaartMusic?: string
  uitvaartDate?: string
  uitvaartTime?: string
  uitvaartLocation?: string
  herinneringText?: string
}

// V49: 'Later delen'-voorkeuren per persoonlijke brief (geen verzendacties)
const SHARE_OPTS: { key: string; label: string }[] = [
  { key: 'pdf',      label: 'Worden afgedrukt als brief (PDF)' },
  { key: 'inperson', label: 'Persoonlijk worden overhandigd' },
  { key: 'wilsapp',  label: 'Beschikbaar zijn in WilsApp voor mijn vertrouwenspersoon' },
  { key: 'service',  label: 'Worden doorgestuurd naar de Berichtenservice' },
]

// V49: berichtcategorieën als inklapbare kaarten. 'letter' = persoonlijke brief met 'Later delen'-blok.
const MSG_CARDS: { id: string; field: keyof PersonalFarewellData; emoji: string; title: string; letter: boolean; recipientLabel: string }[] = [
  { id: 'general',  field: 'generalMessage', emoji: '❤️',       title: 'Algemene afscheidsboodschap', letter: true,  recipientLabel: 'Iedereen die mij dierbaar is' },
  { id: 'partner',  field: 'letterPartner',  emoji: '❤️',       title: 'Bericht aan mijn partner',     letter: true,  recipientLabel: 'Mijn partner' },
  { id: 'children', field: 'letterChildren', emoji: '👨‍👩‍👧', title: 'Bericht aan mijn kinderen',    letter: true,  recipientLabel: 'Mijn kinderen' },
  { id: 'family',   field: 'letterFamily',   emoji: '👪',       title: 'Bericht aan mijn familie',     letter: true,  recipientLabel: 'Familie' },
  { id: 'friends',  field: 'letterFriends',  emoji: '🤝',       title: 'Bericht aan mijn vrienden',    letter: true,  recipientLabel: 'Vrienden' },
  { id: 'practical', field: 'practicalNotes', emoji: '📋',      title: 'Praktische aanwijzingen',      letter: false, recipientLabel: '' },
]

export function PersonalFarewellPage({ embedded = false, saveRef }: { embedded?: boolean; saveRef?: MutableRefObject<(() => Promise<void>) | null> } = {}) {
  const { userId }  = useAuthStore()
  const navigate   = useNavigate()
  const { target, label } = useReturnNav()
  const [data,      setData]      = useState<FarewellExtended>(DEMO as unknown as FarewellExtended)
  const [isLoading, setIsLoading] = useState(true)
  const [status,    setStatus]    = useState<'NOT_STARTED'|'IN_PROGRESS'|'COMPLETE'>('IN_PROGRESS')
  const [openCard,   setOpenCard]   = useState<string | null>('general')
  const [partner,    setPartner]    = useState<PartnerData | null>(null)
  const [children,   setChildren]   = useState<ChildEntry[]>([])
  const [forwarding, setForwarding] = useState<string | null>(null)

  useEffect(() => {
    if (!userId) return
    loadPartner(userId).then(p => { if (p) setPartner(p) })
    loadChildren(userId).then(c => { if (c?.children) setChildren(c.children) })
  }, [userId])

  useEffect(() => {
    if (!userId) return
    loadPersonalFarewell(userId).then(loaded => {
      if (loaded) { setData(loaded as FarewellExtended) }
      setIsLoading(false)
    })
  }, [userId])

  function recalc(d: FarewellExtended) {
    const vals = Object.values(d).filter(v => typeof v === 'string') as string[]
    const n = vals.filter(v => v.trim()).length
    const p = Math.round((n / vals.length) * 100)
    setStatus(p === 0 ? 'NOT_STARTED' : p === 100 ? 'COMPLETE' : 'IN_PROGRESS')
  }

  const save = useCallback(async (d: FarewellExtended) => {
    if (!userId) return
    await savePersonalFarewell(userId, d as PersonalFarewellData)
  }, [userId])

  const { status: saveStatus, saveNow } = useAutosave({ data, onSave: save, enabled: !isLoading })

  // Wizard-embed: registreer saveNow zodat de wizard pending invoer flusht vóór navigatie
  useEffect(() => {
    if (saveRef) saveRef.current = saveNow
    return () => { if (saveRef) saveRef.current = null }
  }, [saveRef, saveNow])

  const upd = useCallback((k: keyof FarewellExtended, v: string) => {
    setData(prev => { const n = { ...prev, [k]: v }; recalc(n); return n })
  }, [])

  function getShare(id: string): string[] {
    return data.meta?.messages?.[id]?.share ?? []
  }
  function toggleShare(id: string, key: string) {
    setData(prev => {
      const messages = { ...(prev.meta?.messages ?? {}) }
      const cur = messages[id]?.share ?? []
      const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key]
      messages[id] = { ...(messages[id] ?? {}), share: next }
      const n = { ...prev, meta: { ...(prev.meta ?? {}), messages } }
      recalc(n); return n
    })
  }
  async function printLetter(title: string, text: string) {
    const { jsPDF } = await import('jspdf')
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    doc.setFontSize(14); doc.text(title, 20, 24)
    doc.setFontSize(11)
    const lines = doc.splitTextToSize(text || '', 170)
    doc.text(lines, 20, 38)
    doc.save(`WilsApp-${title.replace(/[^\w]+/g, '-')}.pdf`)
  }
  // 'Doorsturen naar de Berichtenservice': bereidt een concept voor in de BESTAANDE Berichtenservice (db.messages).
  // Geen tweede berichtenmodel, geen automatische verzending; ontvanger/contactgegevens worden hergebruikt.
  async function forwardToService(card: typeof MSG_CARDS[number]) {
    if (!userId) return
    const text = (data[card.field] as string) ?? ''
    if (!text.trim()) return
    setForwarding(card.id)
    try {
      let recipientLabel = card.recipientLabel
      let recipientPhone = ''
      let recipientEmail = ''
      if (card.id === 'partner' && partner) {
        const pname = [partner.name, partner.lastName].filter(Boolean).join(' ').trim()
        if (pname) recipientLabel = `Mijn partner - ${pname}`
        recipientPhone = partner.phone ?? ''
        recipientEmail = partner.email ?? ''
      }
      if (card.id === 'children' && children.length) {
        const names = children.map(c => c.name).filter(Boolean).join(', ')
        if (names) recipientLabel = `Mijn kinderen - ${names}`
      }
      const existing = await loadMessages(userId)
      const dup = existing.some(m => m.recipientLabel === recipientLabel && m.content.trim() === text.trim())
      if (!dup) {
        await saveMessage(userId, { recipientLabel, content: text, status: 'DRAFT', recipientPhone, recipientEmail })
      }
      if (!getShare(card.id).includes('service')) toggleShare(card.id, 'service')
      await saveNow()
      navigate(ROUTES.MESSAGES)
    } finally {
      setForwarding(null)
    }
  }

  if (isLoading) return <LoadingState message="Laden..." />

  return (
    <div>
      {!embedded && (
        <button onClick={async () => { await saveNow(); navigate(ROUTES.DASHBOARD) }}
          className="flex items-center gap-1.5 text-sm text-ink-4 hover:text-ink-3 transition-colors mb-5 group">
          <svg className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
          Terug naar dashboard
        </button>
      )}
      <ModuleShell title="Persoonlijk afscheid"
        description="Schrijf persoonlijke berichten, plan uw uitvaart en leg uw nalatenschap vast." status={status}
        actions={
          <div className="flex items-center gap-2">
            <InfoButton title="Persoonlijk afscheid"
              content={<>
                <p>Op deze pagina legt u persoonlijke berichten vast voor uw naasten en plant u de details van uw afscheid.</p>
                <p><strong>Afscheidsbrieven:</strong> worden bewaard en zijn later toegankelijk voor uw vertrouwenspersoon.</p>
                <p><strong>Rouwkaart:</strong> maak alvast een concept voor uw rouwkaart of overlijdensbericht.</p>
                <p>Bewerk de voorbeeldteksten naar uw eigen woorden.</p>
              </>} />
            <SaveStatusBadge status={saveStatus} />
          </div>
        }
      >
        {/* V49: warme intro */}
        <div className="rounded-2xl bg-sage-50 border border-sage-100 px-5 py-4">
          <p className="text-sm text-ink-2 leading-relaxed">
            Woorden kunnen troost geven, ook wanneer u er zelf niet meer bent. Op deze pagina kunt u
            persoonlijke boodschappen, herinneringen en praktische aanwijzingen vastleggen voor de
            mensen die u dierbaar zijn.
          </p>
          <p className="text-sm text-ink-3 leading-relaxed mt-2">
            Neem hiervoor gerust de tijd. Alles wordt automatisch opgeslagen en u kunt uw teksten op
            ieder moment aanpassen.
          </p>
        </div>

        {/* V49: persoonlijke berichten als inklapbare kaarten met 'Later delen'-voorkeuren */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-semibold text-ink">Persoonlijke berichten</h2>
            <Badge variant="info" size="sm">Privé</Badge>
          </div>
          <div className="flex flex-col gap-2.5">
            {MSG_CARDS.map(card => {
              const open   = openCard === card.id
              const text   = (data[card.field] as string) ?? ''
              const filled = text.trim().length > 0
              const share  = getShare(card.id)
              return (
                <div key={card.id} className="rounded-2xl border border-sand-200 bg-white overflow-hidden">
                  <button type="button" onClick={() => setOpenCard(open ? null : card.id)}
                    className="flex w-full items-center gap-3 px-4 py-3.5 text-left hover:bg-sand-50 transition-colors">
                    <span className="text-xl flex-shrink-0" aria-hidden="true">{card.emoji}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-ink">{card.title}</span>
                      {filled && !open && (
                        <span className="block text-xs text-ink-4 truncate">{text.replace(/\s+/g, ' ').slice(0, 64)}…</span>
                      )}
                    </span>
                    {filled && <CheckCircle2 className="h-4 w-4 text-sage-500 flex-shrink-0" aria-hidden="true" />}
                    <ChevronRight className={`h-4 w-4 text-ink-4 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden="true" />
                  </button>
                  {open && (
                    <div className="px-4 pb-4 flex flex-col gap-3">
                      <Textarea value={text} onChange={e => upd(card.field, e.target.value)}
                        maxLength={3000} showCount className="min-h-[140px]" />
                      {card.letter && (
                        <div className="rounded-xl bg-sand border border-sand-200 px-3.5 py-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Share2 className="h-3.5 w-3.5 text-ink-3 flex-shrink-0" aria-hidden="true" />
                            <h4 className="text-xs font-semibold text-ink">Later delen - deze boodschap mag later:</h4>
                          </div>
                          <div className="flex flex-col gap-1.5">
                            {SHARE_OPTS.map(o => (
                              <Checkbox key={o.key} label={o.label}
                                checked={share.includes(o.key)}
                                onChange={() => toggleShare(card.id, o.key)} />
                            ))}
                          </div>
                          <p className="text-[11px] text-ink-4 leading-relaxed mt-2">
                            Dit zijn voorkeuren, geen verzendacties. WilsApp verstuurt nooit automatisch iets.
                          </p>
                          <div className="flex flex-wrap gap-2 mt-3">
                            <button type="button" onClick={() => printLetter(card.title, text)} disabled={!filled}
                              className="inline-flex items-center gap-1.5 rounded-lg border border-sand-300 bg-white px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-sand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                              <Printer className="h-3.5 w-3.5" aria-hidden="true" /> Afdrukken (PDF)
                            </button>
                            {share.includes('service') && (
                              <button type="button" onClick={() => forwardToService(card)} disabled={!filled || forwarding === card.id}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-sage-300 bg-sage-50 px-3 py-1.5 text-xs font-medium text-sage-700 hover:bg-sage-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                <Send className="h-3.5 w-3.5" aria-hidden="true" />
                                {forwarding === card.id ? 'Voorbereiden…' : 'Voorbereiden in Berichtenservice'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Rouwkaart-builder */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-semibold text-ink">Rouwkaart</h2>
            <InfoButton title="Rouwkaart"
              content={<>
                <p>Maak alvast een concept voor uw rouwkaart of overlijdensbericht. Uw naasten kunnen dit gebruiken als leidraad.</p>
                <p>U kiest een stijl (modern, klassiek, etc.) en vult persoonlijke tekst, een gedicht of een muzieknummer in.</p>
              </>} />
          </div>
          <Card>
            <div className="flex flex-col gap-4">
              <Select label="Stijl / template" options={ROUWKAART_TEMPLATES} value={data.rouwkaartTemplate ?? ''} onChange={e => upd('rouwkaartTemplate', e.target.value)} placeholder="Kies een stijl..." />
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Naam (zoals op rouwkaart)" value={data.rouwkaartName ?? ''} onChange={e => upd('rouwkaartName', e.target.value)} placeholder="Volledige naam" />
                <Input label="Geboortedatum" type="date" value={data.rouwkaartBirthDate ?? ''} onChange={e => upd('rouwkaartBirthDate', e.target.value)} />
              </div>
              <Textarea label="Persoonlijke tekst of motto" value={data.rouwkaartText ?? ''} onChange={e => upd('rouwkaartText', e.target.value)} placeholder="Bijv: Na een leven vol liefde voor zijn/haar familie..." maxLength={400} showCount />
              <Textarea label="Gedicht (optioneel)" value={data.rouwkaartPoem ?? ''} onChange={e => upd('rouwkaartPoem', e.target.value)} placeholder="Bijv: een favoriet gedicht of zelfgeschreven tekst..." maxLength={300} />
              <Input label="Muzieknummer (optioneel)" value={data.rouwkaartMusic ?? ''} onChange={e => upd('rouwkaartMusic', e.target.value)} placeholder="Bijv: Bach Cello Suite nr. 1, Fields of Gold..." />
            </div>
          </Card>
        </div>

        {/* Uitvaartuitnodiging */}
        <div>
          <h2 className="text-base font-semibold text-ink mb-3">Uitvaartuitnodiging</h2>
          <Card>
            <div className="flex flex-col gap-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Input label="Datum (bij voorkeur)" type="date" value={data.uitvaartDate ?? ''} onChange={e => upd('uitvaartDate', e.target.value)} />
                <Input label="Tijdstip (bij voorkeur)" type="time" value={data.uitvaartTime ?? ''} onChange={e => upd('uitvaartTime', e.target.value)} />
              </div>
              <Input label="Locatie" value={data.uitvaartLocation ?? ''} onChange={e => upd('uitvaartLocation', e.target.value)} placeholder="Bijv: Kapel op begraafplaats X, crematorium Y..." />
            </div>
          </Card>
        </div>

        {/* Herinneringspagina */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <h2 className="text-base font-semibold text-ink">Herinneringspagina</h2>
          </div>
          <Card>
            <Textarea label="Levensverhaal of herdenkingstekst" value={data.herinneringText ?? ''} onChange={e => upd('herinneringText', e.target.value)}
              placeholder="Bijv: Een korte samenvatting van uw leven, voor een digitale herinneringspagina of in memoriam..." maxLength={1000} showCount className="min-h-[120px]" />
          </Card>
        </div>
      
        {status !== 'NOT_STARTED' && (
          <ClosingGuidance title="Uw persoonlijke woorden zijn veilig bewaard">
            U kunt uw boodschappen altijd aanpassen zolang u zelf uw keuzes kunt maken.
          </ClosingGuidance>
        )}

        {/* V49: kruislink naar Berichtenservice */}
        <div className="rounded-2xl bg-sage-50 border border-sage-200 px-4 py-4">
          <p className="text-sm text-ink-2 leading-relaxed mb-2.5">
            Wilt u één of meerdere persoonlijke boodschappen op een later moment laten delen met specifieke personen?
          </p>
          <button type="button" onClick={() => navigate(ROUTES.MESSAGES)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sage-300 bg-white px-4 py-2 text-sm font-medium text-sage-700 hover:bg-sage-50 transition-colors">
            Open de Berichtenservice
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

      {/* Sticky save-status zone - zichtbaar wanneer gebruiker onderaan de pagina scrolt */}
      <div className="sticky bottom-0 left-0 right-0 flex items-center justify-end
                      bg-white/90 backdrop-blur-sm border-t border-sand-200
                      px-4 py-2.5 mt-6 -mx-1 rounded-b-2xl">
        <SaveStatusBadge status={saveStatus} />
      </div>
              <div className="flex flex-col gap-2 mt-2">
          {!embedded && (<>
          <PrimaryActionButton onClick={async () => { await saveNow(); navigate(target) }} icon={<CheckCircle2 className="h-5 w-5" />}>
          {label}
        </PrimaryActionButton>
          <SecondaryActionButton onClick={async () => { await saveNow(); navigate(-1) }}>Terug naar vorige scherm</SecondaryActionButton>
          </>)}
          <RegieCoachBlock page="farewell" />
        </div>
    </ModuleShell>
    </div>
  )
}
